/**
 * In-app confirmation dialog — replaces the browser's native confirm(). Exposes
 * an async `useConfirm()` that resolves true/false, rendered through the shared
 * Modal (so it inherits the focus trap, Esc/backdrop dismiss, and styling).
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "Delete folder?", confirmLabel: "Delete", danger: true })) …
 */

import { createContext, useCallback, useContext, useState } from "react";
import type { ReactNode } from "react";
import { Modal } from "./Modal";

interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const Ctx = createContext<ConfirmFn | null>(null);

interface Pending extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (opts) => new Promise<boolean>((resolve) => setPending({ ...opts, resolve })),
    []
  );

  const close = (value: boolean) => {
    pending?.resolve(value);
    setPending(null);
  };

  return (
    <Ctx.Provider value={confirm}>
      {children}
      {pending && (
        <Modal
          open
          onClose={() => close(false)}
          title={pending.title}
          maxWidth="max-w-sm"
          footer={
            <>
              <button
                type="button"
                onClick={() => close(false)}
                className="text-xs font-medium px-3 py-1.5 rounded border"
                style={{ borderColor: "var(--border)" }}
              >
                {pending.cancelLabel ?? "Cancel"}
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => close(true)}
                className={`text-xs font-medium px-3 py-1.5 rounded text-white ${
                  pending.danger ? "bg-red-600 hover:bg-red-500" : "bg-amber-500 hover:bg-amber-400"
                }`}
              >
                {pending.confirmLabel ?? "Confirm"}
              </button>
            </>
          }
        >
          {pending.message ? (
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              {pending.message}
            </p>
          ) : null}
        </Modal>
      )}
    </Ctx.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}
